/**
 * Skafer Phase 0 contracts — typed schemas.
 *
 * These are the typed models required by the P0-CONTRACTS exit gate
 * ("schemas + typed models + fixtures + contract tests"). They encode the
 * prose contracts in docs/contracts/ as compile-time types plus lightweight
 * runtime guards. No behavior change: nothing in the app imports these yet;
 * they exist to be tested against fixtures and to bind Phase 1 implementation.
 */

/** Provenance kinds — see skier-dna-payload.md §2. */
export type ProvenanceKind =
  | 'customer'    // Skier DNA questionnaire / configurator
  | 'interview'   // rep-asked, on the order
  | 'designer'    // designer interpretation
  | 'derived'     // named versioned rule output
  | 'research'    // research assistant (explicit Apply only)
  | 'ullr-import' // production value imported back
  | 'skafer-import';

/** Provenance tuple: where a value came from + how it was derived. */
export interface Provenance {
  kind: ProvenanceKind;
  /** Stable pointer to the source record (skierdnaId, answerId, orderId, …). */
  sourceId?: string;
  /** Required for kind === 'derived': the named rule + version that produced it. */
  rule?: { id: string; version: string };
  /** Provenance chain: this value interprets the referenced earlier values. */
  derivedFrom?: string[];
  capturedAt?: string; // ISO date
}

/** Production ski types per Decision 003 (skier-dna-payload.md v0.3 §1 #20). */
export const SKI_TYPES = [
  'Off-piste',
  'All-Mountain',
  'Powder',
  'Touring',
  'Carving',
  '50-50',
] as const;
export type SkiType = (typeof SKI_TYPES)[number];

/** Snow-climate regions (Level 1) — see ski-regions.md. */
export interface RegionRef {
  name: string;       // e.g. "Colorado"
  level: 1 | 2;       // Level 1 asked always; Level 2 optional detail
}

/** "Where & how they ski" — the mission (capture per Decision 004). */
export interface WhereSection {
  regions: RegionRef[];
  regionsDetail?: RegionRef[];        // optional drill-down (Level 2)
  dayTypes: string[];                  // 'powder' | 'everyday' (v1 pair)
  terrain: string[];                   // behavior tags: 'groomed runs', 'easy trees', 'moguls', …
  feel: number;                        // 0.0 planted ↔ 1.0 playful
}

/** Body — the fit. Units normative: cm, g, mm. */
export interface BodySection {
  height: number;          // cm
  weight: number;         // grams (matches weightG convention)
  age?: number;
  bootSoleLength?: number; // mm (BSL)
}

/** Their current ski — context (Decision 004: ask, don't infer ability). */
export interface CurrentSkiSection {
  items: Array<{ brand?: string; model?: string; year?: number; length?: number }>; // length cm
  loves: string[];
  wouldChange: string[];
  bindings?: { brand?: string; model?: string };
}

/** Customer-stated starting points; engine treats as soft constraints. */
export interface IntentSection {
  chosenLength?: number;   // cm
  chosenWaist?: number;    // mm
  camber?: 'low' | 'medium' | 'high';
  personality?: string;    // id of 1 of 3 AI-generated personalities
}

/** Engine-derived, designer-visible (never asked). */
export interface DesignDerived {
  skiType: SkiType;
  ability: number;         // derived signal; rule provenance mandatory
}

/** Versioned anchor statements (Skafer-side). */
export interface AnchorsSection {
  missionStatement: string;  // ≤2000 chars; original + designer-refined kept
  skierDescription: string;
}

/** Skier DNA → Skafer engine payload (skier-dna-payload.md v0.3). */
export interface SkierDnaPayload {
  contractVersion: string;   // payload semver, e.g. '1.2'
  customerId: number;
  profile: {
    where: WhereSection;
    body: BodySection;
    currentSki: CurrentSkiSection;
    intent?: IntentSection;
    design?: DesignDerived;
  };
  anchors?: AnchorsSection;
  /** Field-path → provenance. Required for every profile field. */
  provenance: Record<string, Provenance>;
}

/**
 * DesignRecommendation — the engine output schema with per-field source
 * (P0-CONTRACTS scope item 2). Every recommended field names where it came
 * from; nothing in a recommendation is unexplained.
 */
export type RecommendationFieldSource =
  | 'generated'            // engine-derived from profile
  | 'precedent'            // copied from a built-ski/gold-standard record
  | 'designer_override';   // human decision over the engine

export interface RecommendationField<T> {
  value: T;
  source: RecommendationFieldSource;
  /** For precedent: the serial/orderForm the value was copied from. */
  precedentRef?: { serial?: number; orderFormId?: number; sampleName?: string };
  /** For designer_override: who/when. */
  overrideBy?: { actor: string; at: string };
  explanation: string;      // human-readable, shown in the designer workspace
}

export interface DesignRecommendation {
  contractVersion: string;
  payloadRef: string;        // id/version of the SkierDnaPayload it answers
  skiType: RecommendationField<SkiType>;
  length: RecommendationField<number>;        // cm
  noseWidth: RecommendationField<number>;      // mm
  tailWidth: RecommendationField<number>;      // mm
  waistWidthRecommendation: RecommendationField<number>; // mm
  flexPattern: RecommendationField<string>;
  construction: RecommendationField<{
    coreMaterialId?: number;
    overallStiffnessId?: number;
    noseBlockId?: number;
    tailBlockId?: number;
    structuralLayerId?: number;
  }>;
  validity: {
    geometric: 'valid' | 'warning' | 'invalid';
    production: 'valid' | 'warning' | 'invalid' | 'unchecked';
    notes: string[];          // must name the field/quantity at fault
  };
  fallbackUsed: 'none' | 'gold-standard' | 'precedent' | 'defer-to-designer';
  unresolved: string[];       // surfaced instead of forcing a result
}

/** DesignHandoff — mutable Skafer→Ullr handoff (handoff-contract.md §3, Decision 001). */
export interface DesignHandoff {
  id: number;
  orderId: number;
  proposalVersionId: string;
  revisionOfHandoffId: number | null;   // null = initial; chains revisions
  status: 'sent' | 'acknowledged' | 'superseded' | 'withdrawn';
  payload: {
    contractVersion: string;
    proposalVersionId: string;
    designSnapshot: { orderForm: Record<string, unknown> };
    changeSummary?: string;
  };
  sentBy: number;
  sentAt: string;                      // ISO
  acknowledgedBy?: number;
  acknowledgedAt?: string;
  supersededByHandoffId?: number;
}

/** OrderForm fields Skafer may write via handoff (handoff-contract.md §4). */
export const SKAFER_OWNED_ORDERFORM_FIELDS = [
  'length', 'noseWidth', 'tailWidth', 'waistWidthRecommendation',
  'flexPattern',
  'coreMaterialId', 'overallStiffnessId', 'noseBlockId', 'tailBlockId',
  'structuralLayerId', 'sidewallTypeId', 'baseMaterialId',
  'handcraftedFor', 'intro', 'skiNotes',
] as const;

/** Factory/Shopify-owned fields a handoff must never write (fail closed). */
export const FACTORY_OWNED_ORDERFORM_FIELDS = [
  'serialNum', 'shopifyDraftOrderId', 'shopifyOrderId', 'invoicePaid',
  'noseInwardAdjustment', 'tailInwardAdjustment',
  'pressOrder', 'shipOrder',
  'actualPress', 'actualMount', 'actualFinish', 'actualShip', 'actualGraphic',
  'scheduledPress', 'desiredArrival', 'locked',
] as const;

export type SkaferOwnedOrderFormField = (typeof SKAFER_OWNED_ORDERFORM_FIELDS)[number];
export type FactoryOwnedOrderFormField = (typeof FACTORY_OWNED_ORDERFORM_FIELDS)[number];

export function isSkaferOwnedField(field: string): field is SkaferOwnedOrderFormField {
  return (SKAFER_OWNED_ORDERFORM_FIELDS as readonly string[]).includes(field);
}

export function isFactoryOwnedField(field: string): field is FactoryOwnedOrderFormField {
  return (FACTORY_OWNED_ORDERFORM_FIELDS as readonly string[]).includes(field);
}

/**
 * Handoff field filter — fail closed: a handoff snapshot may contain ONLY
 * Skafer-owned fields. Any factory-owned or unknown field invalidates it.
 */
export function validateHandoffSnapshot(
  snapshot: Record<string, unknown>,
): { ok: true } | { ok: false; violations: string[] } {
  const violations: string[] = [];
  for (const field of Object.keys(snapshot)) {
    if (isFactoryOwnedField(field)) {
      violations.push(`factory-owned field in snapshot: ${field}`);
    } else if (!isSkaferOwnedField(field)) {
      violations.push(`unknown field in snapshot: ${field}`);
    }
  }
  return violations.length ? { ok: false, violations } : { ok: true };
}

/** Revision-chain check: linear, append-only, single successor. */
export function validateHandoffChain(
  handoffs: DesignHandoff[],
): { ok: true } | { ok: false; violations: string[] } {
  const violations: string[] = [];
  const byId = new Map(handoffs.map((h) => [h.id, h]));
  const revising = new Map<number, number>(); // predecessor id -> successor id
  for (const h of handoffs) {
    if (h.revisionOfHandoffId !== null && !byId.has(h.revisionOfHandoffId)) {
      violations.push(`handoff ${h.id} revises missing handoff ${h.revisionOfHandoffId}`);
    }
    // Linearity: a predecessor may be revised by at most ONE successor.
    if (h.revisionOfHandoffId !== null) {
      const prev = revising.get(h.revisionOfHandoffId);
      if (prev !== undefined && prev !== h.id) {
        violations.push(
          `handoff ${h.revisionOfHandoffId} revised by more than one handoff (${prev} and ${h.id}) — non-linear chain`,
        );
      } else {
        revising.set(h.revisionOfHandoffId, h.id);
      }
    }
    if (h.status === 'superseded') {
      if (!h.supersededByHandoffId || !byId.has(h.supersededByHandoffId)) {
        violations.push(`superseded handoff ${h.id} lacks a valid successor`);
      } else {
        const succ = byId.get(h.supersededByHandoffId);
        if (succ && succ.revisionOfHandoffId !== h.id) {
          violations.push(`successor ${succ.id} does not point back at ${h.id}`);
        }
      }
    }
  }
  return violations.length ? { ok: false, violations } : { ok: true };
}
